import { useMemo } from 'react';

import { FilterConfig, TableView } from '@components/table';
import { TableStore } from '@components/table/table-store';
import { useViewsManager } from '@components/table/use-views-manager';
import { useEvent } from '@hooks/use-event';

import { useInvalidateFavorites } from 'api/favorites/hooks';
import { JourneyTableView } from 'api/journeys/requests';
import {
  tableViewToConfig,
  useCreateTableView,
  useDeleteTableView,
  useUpdateTableView,
  useUpdateTableViewPreferences,
} from 'api/table-views/hooks';

import { ExtraStateStore } from './extra-state-store';

type UseJourneyViewsProps = {
  journeyShortId: string;
  views: Array<JourneyTableView> | undefined;
  selectedViewId: string | undefined;
  extraStateStore: ExtraStateStore;
  tableStore: TableStore;
  filterConfig?: FilterConfig;
};

export const useJourneyViews = ({
  journeyShortId,
  views,
  selectedViewId,
  extraStateStore,
  tableStore,
  filterConfig,
}: UseJourneyViewsProps) => {
  const { invalidateFavorites } = useInvalidateFavorites();
  const queryParams = { tableType: 'journey' as const, tableObjectShortId: journeyShortId };

  const createViewMutation = useCreateTableView(queryParams);
  const updateViewMutation = useUpdateTableView(queryParams);
  const deleteViewMutation = useDeleteTableView(queryParams);
  const updatePreferencesMutation = useUpdateTableViewPreferences(queryParams);

  const onViewChange = useEvent((view: TableView) => {
    const journeyView = view as JourneyTableView;
    if (journeyView.extraState) {
      extraStateStore.actions.sync(journeyView.extraState);
    } else {
      extraStateStore.actions.sync({ addedActionIds: [], customNames: {} });
    }
  });

  const getExtraViewData = useEvent(() => ({
    extraState: extraStateStore.actions.getState(),
  }));

  const transformViewBeforeSave = useEvent((view: JourneyTableView) => ({
    ...view,
    extraState: extraStateStore.actions.getState(),
  }));

  const onSaveView = useEvent((viewId: string, config: Record<string, any>) => {
    updateViewMutation.mutate({ shortId: viewId, data: { config } });
  });

  const onCreateView = useEvent(
    (view: { name: string; isCollaborative?: boolean; config: Record<string, any> }) => {
      createViewMutation.mutate({
        name: view.name,
        isCollaborative: view.isCollaborative,
        config: view.config,
        tableType: 'journey',
        tableObjectShortId: journeyShortId,
      });
    }
  );

  const onUpdateView = useEvent((view: JourneyTableView) => {
    if (!view.id) return;
    const config = tableViewToConfig(view);
    updateViewMutation.mutate(
      {
        shortId: view.id,
        data: { name: view.name, isCollaborative: view.isCollaborative, config },
      },
      { onSuccess: invalidateFavorites }
    );
  });

  const onDeleteView = useEvent((viewId: string) => {
    deleteViewMutation.mutate(viewId, {
      onSuccess: invalidateFavorites,
    });
  });

  const onSelectView = useEvent((viewId: string) => {
    updatePreferencesMutation.mutate({ selectedView: viewId });
  });

  const onReorderViews = useEvent((newOrder: Array<string>) => {
    updatePreferencesMutation.mutate({ viewOrder: newOrder });
  });

  const addedActionIds = extraStateStore.useAddedActionIds();
  const customNames = extraStateStore.useCustomNames();

  const hasExtraUnsavedChanges = useMemo(() => {
    const selectedView = views?.find((view) => view.id === selectedViewId) ?? views?.[0];
    if (!selectedView) return false;
    const saved = selectedView.extraState ?? { addedActionIds: [], customNames: {} };
    return (
      JSON.stringify(addedActionIds) !== JSON.stringify(saved.addedActionIds) ||
      JSON.stringify(customNames) !== JSON.stringify(saved.customNames)
    );
  }, [views, selectedViewId, addedActionIds, customNames]);

  return useViewsManager<JourneyTableView>({
    views,
    selectedViewId,
    tableStore,
    filterConfig,
    hasExtraUnsavedChanges,
    onViewChange,
    getExtraViewData,
    transformViewBeforeSave,
    onSaveView,
    onCreateView,
    onUpdateView,
    onDeleteView,
    onSelectView,
    onReorderViews,
  });
};
